import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { X, Edit2, Trash2, MessageCircle, Share2 } from 'lucide-react';
import { PortfolioItem, Item } from '../../lib/api';
import { ItemDetailModal } from '../ItemDetailModal';
import { SwipeCardTemplate, Badge } from '../SwipeCardTemplate';
import { detectMediaPreview, MediaPreviewInfo } from '../../lib/media';
import { formatPrice } from '../../lib/i18n';
import { haptic } from '../../lib/telegram';

interface OwnerInfo {
  id: number;
  name?: string | null;
  username?: string | null;
  avatar?: string | null;
}

interface PortfolioSwipeCardModalProps {
  item: PortfolioItem;
  owner: OwnerInfo;
  onClose: () => void;
  isOwnProfile?: boolean;
  onEdit?: (item: PortfolioItem) => void;
  onDelete?: (item: PortfolioItem) => void;
  onWrite?: (item: PortfolioItem) => void;
}

const allowedCategories: Item['category'][] = ['beat', 'cover', 'feature', 'mix', 'kit', 'service'];

const getFallbackCover = (title?: string | null) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(title || 'Splyt')}&size=800&background=1a1f2e&color=00FF88`;

const mapPortfolioToItem = (portfolio: PortfolioItem, owner: OwnerInfo): Item => {
  const categoryInput = portfolio.category || undefined;
  const category = allowedCategories.includes(categoryInput as Item['category'])
    ? (categoryInput as Item['category'])
    : 'service';

  const priceKopecks = typeof portfolio.price_uah === 'number' ? portfolio.price_uah : 0;

  return {
    id: portfolio.item_id ? String(portfolio.item_id) : `portfolio-${owner.id}`,
    creator_id: owner.id,
    category,
    title: portfolio.title || 'Portfolio Item',
    description: portfolio.description || '',
    price_uah: priceKopecks,
    tags: portfolio.type ? [portfolio.type] : [],
    bpm: undefined,
    key: undefined,
    formats: [],
    preview_url: portfolio.preview_url || portfolio.value || portfolio.link || '',
    media_url: portfolio.preview_url || portfolio.value || portfolio.link || '',
    cover_url: portfolio.cover_url || getFallbackCover(portfolio.title),
    is_exclusive: false,
    status: 'active',
    created_at: portfolio.created_at || new Date().toISOString(),
    creator: {
      id: owner.id,
      name: owner.name || owner.username || 'Creator',
      username: owner.username || undefined,
      avatar: owner.avatar || undefined,
    },
    stats: {
      likes: 0,
      plays: 0,
    },
  };
};

export function PortfolioSwipeCardModal({ item, owner, onClose, isOwnProfile = false, onEdit, onDelete, onWrite }: PortfolioSwipeCardModalProps) {
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [activeMediaInfo, setActiveMediaInfo] = useState<MediaPreviewInfo | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const controlsFadeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const mappedItem = useMemo(() => mapPortfolioToItem(item, owner), [item, owner]);

  const previewSource = useMemo(() => {
    return item.preview_url || item.value || item.link || undefined;
  }, [item.preview_url, item.value, item.link]);

  const baseMediaInfo = useMemo(() => detectMediaPreview(previewSource), [previewSource]);

  // Auto-play on mount if media is available
  useEffect(() => {
    if (baseMediaInfo.type !== 'none' && baseMediaInfo.type !== 'direct-link') {
      setActiveMediaInfo(baseMediaInfo);
      setIsPlayingPreview(true);
    }
  }, [baseMediaInfo]);

  // Keep media info active once set
  const effectiveMediaInfo = useMemo(() => {
    return activeMediaInfo || baseMediaInfo;
  }, [activeMediaInfo, baseMediaInfo]);

  // Cleanup on unmount or item change
  useEffect(() => {
    return () => {
      const node = mediaRef.current;
      if (node) {
        try {
          node.pause();
        } catch (error) {
          // ignore on unmount
        }
      }
      // Clear timers
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
      if (controlsFadeTimerRef.current) {
        clearTimeout(controlsFadeTimerRef.current);
      }
    };
  }, [item]);

  // Control media playback
  useEffect(() => {
    const node = mediaRef.current;
    if (!node) return;

    if (isPlayingPreview) {
      node.play().catch(() => {});
    } else {
      try {
        node.pause();
      } catch (error) {
        // ignore
      }
    }
  }, [isPlayingPreview]);

  // Remove this old effectiveMediaInfo - already defined above

  const handleCardClick = () => {
    // Single click - toggle controls visibility
    setShowControls(true);
    
    // Auto-hide controls after 3 seconds
    if (controlsFadeTimerRef.current) {
      clearTimeout(controlsFadeTimerRef.current);
    }
    controlsFadeTimerRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (baseMediaInfo.type === 'none') {
      haptic.light();
      return;
    }

    if (baseMediaInfo.type === 'direct-link' && baseMediaInfo.rawUrl) {
      haptic.light();
      window.open(baseMediaInfo.rawUrl, '_blank');
      return;
    }

    haptic.medium();
    setIsPlayingPreview(prev => !prev);
    
    // Keep controls visible briefly
    setShowControls(true);
    if (controlsFadeTimerRef.current) {
      clearTimeout(controlsFadeTimerRef.current);
    }
    controlsFadeTimerRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  let mediaInner: ReactNode = null;
  if (activeMediaInfo) {
    switch (effectiveMediaInfo.type) {
      case 'youtube':
      case 'spotify':
      case 'soundcloud':
        if (effectiveMediaInfo.embedUrl) {
          mediaRef.current = null;
          mediaInner = (
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-black/40 backdrop-blur-sm">
              <iframe
                key={`${mappedItem.id}-${effectiveMediaInfo.embedUrl}`}
                src={effectiveMediaInfo.embedUrl}
                className="w-full"
                style={{ minHeight: effectiveMediaInfo.type === 'spotify' ? 180 : 200 }}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        break;
      case 'video':
        if (effectiveMediaInfo.embedUrl) {
          mediaInner = (
            <video
              ref={(node) => {
                mediaRef.current = node;
              }}
              src={effectiveMediaInfo.embedUrl}
              className="w-full h-full absolute inset-0 object-cover"
              autoPlay
              loop
              playsInline
              muted={false}
            />
          );
        }
        break;
      case 'audio':
        if (effectiveMediaInfo.embedUrl) {
          mediaInner = (
            <audio
              ref={(node) => {
                mediaRef.current = node;
              }}
              src={effectiveMediaInfo.embedUrl}
              autoPlay
              loop
            />
          );
        }
        break;
      default:
        break;
    }
  }

  // Only show media content for embeddable platforms (not for native audio/video)
  const mediaContent = mediaInner && (effectiveMediaInfo.type === 'youtube' || effectiveMediaInfo.type === 'spotify' || effectiveMediaInfo.type === 'soundcloud') ? (
    <div className="bg-black/20 border border-white/10 rounded-2xl px-4 py-3">
      {mediaInner}
    </div>
  ) : undefined;

  // Render native video/audio directly in the background
  const backgroundMedia = mediaInner && (effectiveMediaInfo.type === 'video' || effectiveMediaInfo.type === 'audio') ? mediaInner : null;

  const badges: Badge[] = [];
  if (item.type) {
    badges.push({ text: item.type.toUpperCase(), variant: 'green' });
  }
  if (item.source === 'marketplace') {
    badges.push({ text: 'Marketplace', variant: 'purple' });
  }

  const hasPrice = typeof mappedItem.price_uah === 'number' && mappedItem.price_uah > 0;
  const formattedPrice = hasPrice ? formatPrice(mappedItem.price_uah / 100) : '';
  const playButtonIcon = baseMediaInfo.type === 'direct-link' ? 'link' : 'play';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm"
        onClick={(e) => {
          e.stopPropagation();
          handleCardClick();
        }}
      >
        {/* Edit/Delete buttons for own posts */}
        {isOwnProfile && (
          <div className="absolute -top-12 left-0 flex gap-2 z-10">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.medium();
                  onEdit(item);
                }}
                className="w-10 h-10 rounded-full flex items-center justify-center glass border border-white/20 hover:bg-white/10 transition-all"
              >
                <Edit2 className="w-5 h-5 text-white" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm('Видалити цей пост?')) {
                    haptic.medium();
                    onDelete(item);
                  }
                }}
                className="w-10 h-10 rounded-full flex items-center justify-center glass border border-red-500/20 hover:bg-red-500/10 transition-all"
              >
                <Trash2 className="w-5 h-5 text-red-400" />
              </button>
            )}
          </div>
        )}

        <div className="absolute -top-12 right-0 flex items-center gap-2 z-10">
          {!isOwnProfile && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                haptic.medium();
                if (onWrite) {
                  onWrite(item);
                }
              }}
              className="w-10 h-10 rounded-full flex items-center justify-center glass border border-white/20 hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5 text-white" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              haptic.light();
              onClose();
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center glass border border-white/20 hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="absolute -bottom-12 right-0 flex items-center gap-2 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              haptic.light();
              const text = `Check out this item: ${item.title}`;
              const url = window.location.href;
              if (navigator.share) {
                navigator.share({
                  title: item.title,
                  text: text,
                  url: url,
                });
              } else {
                // Fallback for browsers that don't support navigator.share
                const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
                window.open(telegramUrl, '_blank');
              }
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center glass border border-white/20 hover:bg-white/10 transition-all"
          >
            <Share2 className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="relative h-[480px]">
          {/* Background video/audio if present */}
          {backgroundMedia}

          <div className="absolute inset-0 z-20">
            <SwipeCardTemplate
            item={mappedItem}
            swiping={null}
            formattedPrice={formattedPrice}
            onPlayClick={handlePlayClick}
            onInfoClick={(e) => {
              e.stopPropagation();
              haptic.light();
                setShowInfoModal(true);
            }}
            title={mappedItem.title}
            subtitle={mappedItem.creator?.username ? `@${mappedItem.creator.username}` : mappedItem.creator?.name}
            description={mappedItem.description}
            badges={badges}
            showPrice={hasPrice}
            showInfoButton={true}
            playButtonStyle="default"
            categoryLabel={item.category || item.type || 'Portfolio'}
            alwaysShowPlayButton={false}
            isPlaying={isPlayingPreview}
            playButtonIcon={playButtonIcon}
            mediaContent={mediaContent}
            showPlayButton={showControls}
          />
          </div>

          
        </div>
      </div>
      {showInfoModal && (
        <ItemDetailModal 
          item={mappedItem} 
          onClose={() => setShowInfoModal(false)}
          onLike={() => {
            setIsLiked(!isLiked);
            haptic.light();
          }}
          isLiked={isLiked}
          onMessageClick={() => {
            setShowInfoModal(false);
            if (onWrite) {
              onWrite(item);
            }
          }}
        />
      )}
    </div>
  );
}
